import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import { bySlug } from "../content/articles";
import { SEO_SITE_URL } from "../lib/siteConfig";
import { Blocks, Inline, ProductCards, LeadForm, RelatedCards } from "../components/journal/ArticleKit";
import { ArticleFigure } from "../components/journal/ArticleFigure";
import { ARTICLE_MEDIA, ARTICLE_TOPICS, EDITORIAL_FALLBACK, figureInfo } from "../content/articleImages";

const imgObj = (origin, f) => ({
  "@type": "ImageObject", url: `${origin}${f.src}`, width: f.width, height: f.height,
  caption: f.caption || f.alt,
});

function countWords(a) {
  const chunks = [a.answer];
  a.body.forEach((b) => {
    if (b.p) chunks.push(b.p);
    if (b.h2) chunks.push(b.h2);
    if (b.h3) chunks.push(b.h3);
    if (b.ul) chunks.push(b.ul.join(" "));
    if (b.table) chunks.push(b.table.headers.join(" ") + " " + b.table.rows.map((r) => r.join(" ")).join(" "));
  });
  a.faqs.forEach((f) => chunks.push(f.q + " " + f.a));
  return chunks.join(" ").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").split(/\s+/).filter(Boolean).length;
}

export default function JournalArticle() {
  const { slug } = useParams();
  const a = bySlug[slug];
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const url = `${SEO_SITE_URL}/journal/${a ? a.slug : ""}`;
  const media = a ? ARTICLE_MEDIA[a.slug] : null;
  const bodyMedia = media ? (media.secondary || EDITORIAL_FALLBACK) : null;
  const primaryFig = media ? figureInfo(media.primary) : null;
  const bodyFig = bodyMedia ? figureInfo(bodyMedia) : null;
  const jsonLd = a ? [
    {
      "@context": "https://schema.org", "@type": "BlogPosting",
      headline: a.title, description: a.metaDescription,
      // only images actually rendered on this article: hero + the one in-body figure
      image: [
        ...(primaryFig ? [imgObj(SEO_SITE_URL, primaryFig)] : []),
        ...(bodyFig ? [imgObj(SEO_SITE_URL, bodyFig)] : []),
      ],
      ...(ARTICLE_TOPICS[a.slug] ? { about: { "@type": "Thing", name: ARTICLE_TOPICS[a.slug] } } : {}),
      ...(a.products?.length ? { mentions: a.products.map((s) => ({ "@id": `${SEO_SITE_URL}/products/${s}#product` })) } : {}),
      author: { "@type": "Organization", "@id": `${SEO_SITE_URL}/#organization`, name: "Tiff. Wellness Co.", url: `${SEO_SITE_URL}/` },
      publisher: { "@type": "Organization", "@id": `${SEO_SITE_URL}/#organization`, name: "Tiff. Wellness Co.", url: `${SEO_SITE_URL}/` },
      datePublished: a.datePublished, dateModified: a.dateModified,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      url, inLanguage: "en-US", articleSection: a.section, wordCount: countWords(a),
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SEO_SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Journal", item: `${SEO_SITE_URL}/journal` },
        { "@type": "ListItem", position: 3, name: a.title, item: url },
      ],
    },
  ] : null;
  usePageMeta(a ? a.metaTitle : "Journal", a ? a.metaDescription : "", { jsonLd, ...(primaryFig ? { ogImage: primaryFig.src } : {}) });

  if (!a) return <Navigate to="/journal" replace />;
  const published = new Date(a.datePublished);

  return (
    <article className="max-w-3xl mx-auto px-4 py-12" data-testid={`journal-article-${a.slug}`}>
      <nav aria-label="Breadcrumb" className="text-xs text-[#171715]/60 mb-5" data-testid="article-breadcrumb">
        <Link to="/" className="underline">Home</Link> <span aria-hidden>›</span> <Link to="/journal" className="underline">Journal</Link> <span aria-hidden>›</span> <span>{a.section}</span>
      </nav>
      <p className="eyebrow mb-3">{a.section}</p>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4">{a.title}</h1>
      <p className="text-xs text-[#171715]/60 mb-6" data-testid="article-byline">
        By the Tiff. Wellness Co. editorial team · Published <time dateTime={a.datePublished}>{published.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</time>
        {a.dateModified !== a.datePublished && <> · Updated <time dateTime={a.dateModified}>{new Date(a.dateModified).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</time></>}
      </p>
      {media && <ArticleFigure media={media.primary} eager testId="article-figure-hero" />}
      <div className="card-paper p-5 mb-8 border-l-4 border-[#A35847]" data-testid="article-answer-box">
        <p className="text-[15px] leading-relaxed font-medium"><Inline text={a.answer} /></p>
      </div>

      <Blocks blocks={a.body.slice(0, 6)} />
      {bodyMedia && <ArticleFigure media={bodyMedia} testId="article-figure-body" />}
      <Blocks blocks={a.body.slice(6)} />
      <ProductCards slugs={a.products} articleSlug={a.slug} />

      <section data-testid="article-faqs" className="mt-10">
        <h2 className="text-xl md:text-2xl font-medium mb-4">Frequently asked questions</h2>
        {a.faqs.map((f, i) => (
          <details key={i} className="border-b border-[#EBD9D0] py-3" open>
            <summary className="font-medium text-sm cursor-pointer">{f.q}</summary>
            <p className="text-sm text-[#171715]/80 mt-2 leading-relaxed"><Inline text={f.a} /></p>
          </details>
        ))}
      </section>

      {a.sources?.length > 0 && (
        <section className="mt-8 text-xs text-[#171715]/65" data-testid="article-sources">
          <h2 className="font-medium text-sm mb-2">Sources</h2>
          <ul className="list-disc pl-5 space-y-1">
            {a.sources.map((s, i) => <li key={i}><a href={s.url} target="_blank" rel="noopener noreferrer" className="underline">{s.label}</a></li>)}
          </ul>
        </section>
      )}

      <p className="text-xs text-[#171715]/55 mt-6 border-t border-[#EBD9D0] pt-4" data-testid="article-editorial-note">
        Editorial note: written and maintained by Tiff. Wellness Co. (the company) based on our posted menu and the cited public sources. We don't provide medical or nutrition advice, and we flag anything we haven't verified — including supplement doses our menu doesn't list.
      </p>

      <LeadForm articleSlug={a.slug} />
      <RelatedCards slugs={a.related} />
    </article>
  );
}
